import React, { useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { formatDisplayDateTime } from '../../utils/dateTimeFormat';

interface RTUDiagnosticsTabbedTreeProps {
  rtuDiagnostics: any;
}

interface TreeNode {
  key: string;
  data: any;
  children?: TreeNode[];
}

const isRecord = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toCellText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(toCellText).filter(Boolean).join(', ');
  }

  if (!isRecord(value)) {
    return '';
  }

  if (typeof value.status === 'string' && value.status.trim()) {
    return value.status;
  }

  if (typeof value.config === 'string' && value.config.trim()) {
    return value.config;
  }

  const total =
    typeof value.total === 'number'
      ? value.total
      : typeof value.total === 'string' && value.total.trim() && !Number.isNaN(Number(value.total))
        ? Number(value.total)
        : null;

  if (total !== null) {
    return `${total} items`;
  }

  if (isRecord(value.dataEntries)) {
    return `${Object.keys(value.dataEntries).length} items`;
  }

  if (isRecord(value.entries)) {
    return `${Object.keys(value.entries).length} items`;
  }

  return '';
};

const isLikelyIpAddress = (value: string): boolean =>
  /^\d{1,3}(?:\.\d{1,3}){3}$/.test(value.trim());

const expandAll = (nodes: TreeNode[], acc: Record<string, boolean> = {}) => {
  nodes.forEach((n) => {
    acc[n.key] = true;
    if (n.children) expandAll(n.children, acc);
  });
  return acc;
};

const RTUDiagnosticsTabbedTree: React.FC<RTUDiagnosticsTabbedTreeProps> = ({ rtuDiagnostics }) => {
  const { t } = useTranslation();
  const humanizeLabel = (value: string) => value.replace(/([a-z])([A-Z])/g, '$1 $2');
  const dataFileLabel = (value: string) => {
    const labels: Record<string, string> = {
      rbeSetpoint: 'RBE Setpoint',
      smartData: 'Smart Data',
      realtimeData: 'Realtime Data',
    };

    return labels[value] ?? humanizeLabel(value);
  };
  const sortNaturally = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  const diagnosticLabel = (value: string) => {
    const labels: Record<string, string> = {
      io: 'I/O',
      dms: 'DMS',
      hartGateway: 'HART Gateway',
      loraGateway: 'LoRa Gateway',
      liveFeed: 'Live Feed',
      secondaryRtu: 'Secondary RTU',
      vpn: 'VPN',
      modem: 'Modem',
      modbusSlaveSerial: 'Modbus Slave Serial',
      modbusSlaveTcp: 'Modbus Slave TCP',
    };

    return labels[value] ?? humanizeLabel(value);
  };

  const commNodes = useMemo<TreeNode[]>(() => {
    if (!rtuDiagnostics?.communication) return [];
    const nodes: TreeNode[] = [];
    const comm = rtuDiagnostics.communication;

    const push = (key: string, entry: any) => {
      nodes.push({
        key: `comm-${key}`,
        data: {
          name: diagnosticLabel(key),
          config: toCellText(entry?.config),
          status: toCellText(entry?.status),
          ip: toCellText(entry?.ip),
          type: toCellText(entry?.type),
          com: toCellText(entry?.com),
          query: toCellText(entry?.query),
          response: toCellText(entry?.response),
          timeout: toCellText(entry?.timeout),
          invalidResponse: toCellText(entry?.invalidResponse),
          lastQueryTime: formatDisplayDateTime(entry?.lastQueryTime),
          lastResponseTime: formatDisplayDateTime(entry?.lastResponseTime),
        },
      });
    };

    Object.keys(comm).forEach((k) => {
      if (k === 'modbusDevices' && comm.modbusDevices?.devices) {
        Object.keys(comm.modbusDevices.devices).forEach((d) => push(d, comm.modbusDevices.devices[d]));
      } else if (isRecord(comm[k])) {
        push(k, comm[k]);
      }
    });
    return nodes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtuDiagnostics]);

  const dfgNodes = useMemo<TreeNode[]>(() => {
    if (!rtuDiagnostics?.dataFileGeneration) return [];
    const catNodes: TreeNode[] = [];
    const dfg = rtuDiagnostics.dataFileGeneration;
    Object.keys(dfg).forEach((cat) => {
      const entries = dfg[cat]?.dataEntries || {};
      const children: TreeNode[] = Object.keys(entries).map((ek) => ({
        key: `dfg-${cat}-${ek}`,
        data: {
          name: toCellText(entries[ek]?.groupName) || ek,
          rtuId: toCellText(entries[ek]?.rtuId),
          status: toCellText(entries[ek]?.status ?? entries[ek]),
          type: toCellText(entries[ek]?.type),
        },
      }));
      catNodes.push({
        key: `dfg-${cat}`,
        data: { name: dataFileLabel(cat) },
        children,
      });
    });
    return catNodes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtuDiagnostics]);

  const ftNodes = useMemo<TreeNode[]>(() => {
    if (!rtuDiagnostics?.fileTransfer) return [];
    const ft = rtuDiagnostics.fileTransfer;
    const sectionLabel = (cat: string) => {
      const labels: Record<string, string> = {
        fileTransferEntries: t('rtuDetail.diagnosticsTabs.groups.transferProfiles'),
        fileTransferEntryObjects: t('rtuDetail.diagnosticsTabs.groups.transferTargets'),
        FTP: t('rtuDetail.diagnosticsTabs.groups.ftp'),
        MQTT: t('rtuDetail.diagnosticsTabs.groups.mqtt'),
      };

      return labels[cat] ?? humanizeLabel(cat);
    };

    const pack = (cat: string, obj: any) => {
      const children: TreeNode[] = [];
      Object.keys(obj).sort(sortNaturally).forEach((k) => {
        const v = obj[k];
        const status = toCellText((isRecord(v) ? v.status : undefined) ?? v);
        const inferredIp =
          isRecord(v) && !v.ip && k.toLowerCase().includes('ip') && isLikelyIpAddress(status)
            ? status
            : '';
        children.push({
          key: `ft-${cat}-${k}`,
          data: {
            name: k.toUpperCase(),
            status,
            type: toCellText(isRecord(v) ? v.type : ''),
            config: toCellText(isRecord(v) ? v.config : ''),
            ip: toCellText(isRecord(v) ? v.ip : '') || inferredIp,
            primaryIp: toCellText(isRecord(v) ? v.primaryIp : ''),
            secondaryIp: toCellText(isRecord(v) ? v.secondaryIp : ''),
          },
        });
      });
      return {
        key: `ft-${cat}`,
        data: { name: sectionLabel(cat) },
        children,
      };
    };

    const nodes: TreeNode[] = [];
    if (ft.fileTransferEntries) nodes.push(pack('fileTransferEntries', ft.fileTransferEntries));
    if (ft.fileTransferEntryObjects) nodes.push(pack('fileTransferEntryObjects', ft.fileTransferEntryObjects));
    if (isRecord(ft.ftp) && isRecord(ft.ftp.entries)) nodes.push(pack('FTP', ft.ftp.entries));
    if (isRecord(ft.mqtt) && isRecord(ft.mqtt.entries)) nodes.push(pack('MQTT', ft.mqtt.entries));
    return nodes;
  }, [rtuDiagnostics, t]);

  const [commExpanded, setCommExpanded] = useState<Record<string, boolean>>(() => expandAll(commNodes));
  const [dfgExpanded, setDfgExpanded] = useState<Record<string, boolean>>(() => expandAll(dfgNodes));
  const [ftExpanded, setFtExpanded] = useState<Record<string, boolean>>(() => expandAll(ftNodes));
  const [showFilters, setShowFilters] = useState(false);

  const cardTitle = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{t('rtuDetail.sections.diagnostics')}</span>
      <Button
        icon="pi pi-search"
        className="p-button-text p-button-sm"
        onClick={() => setShowFilters(!showFilters)}
        tooltip={showFilters ? t('search.hide') : t('search.show')}
        tooltipOptions={{ position: 'left' }}
        style={{ padding: '0.15rem 0.3rem', width: 'auto', height: 'auto', color: '#333' }}
      />
    </div>
  );

  return (
    <Card title={cardTitle} className="compact-card flat-card">
      <TabView>
        <TabPanel header={t('rtuDetail.diagnosticsTabs.headers.connections')}>
          <div style={{ overflow: 'auto' }}>
            <TreeTable 
              value={commNodes} 
              expandedKeys={commExpanded} 
              onToggle={(e) => setCommExpanded(e.value)} 
              showGridlines 
              style={{ width: '100%', fontSize: '0.8rem' }}
              tableStyle={{ tableLayout: 'fixed', minWidth: '1180px' }}
              filterMode="lenient"
            >
              <Column field="name" header={t('rtuDetail.diagnosticsTabs.columns.name')} expander style={{ width: '190px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="config" header={t('rtuDetail.diagnosticsTabs.columns.configuration')} style={{ width: '120px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="status" header={t('rtuDetail.diagnosticsTabs.columns.status')} style={{ width: '110px' }} headerStyle={{ textAlign: 'center' }} bodyStyle={{ textAlign: 'center' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="ip" header={t('rtuDetail.diagnosticsTabs.columns.ipAddress')} style={{ width: '140px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="type" header={t('rtuDetail.diagnosticsTabs.columns.type')} style={{ width: '90px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="com" header={t('rtuDetail.diagnosticsTabs.columns.port')} style={{ width: '90px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="query" header={t('rtuDetail.diagnosticsTabs.columns.query')} style={{ width: '80px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="response" header={t('rtuDetail.diagnosticsTabs.columns.response')} style={{ width: '90px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="timeout" header={t('rtuDetail.diagnosticsTabs.columns.timeout')} style={{ width: '90px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="invalidResponse" header={t('rtuDetail.diagnosticsTabs.columns.invalidResponse')} style={{ width: '120px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="lastQueryTime" header={t('rtuDetail.diagnosticsTabs.columns.lastQueryTime')} style={{ width: '130px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="lastResponseTime" header={t('rtuDetail.diagnosticsTabs.columns.lastResponseTime')} style={{ width: '130px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
            </TreeTable>
          </div>
        </TabPanel>
        <TabPanel header={t('rtuDetail.diagnosticsTabs.headers.dataFiles')}>
          <div style={{ overflow: 'auto' }}>
            <TreeTable 
              value={dfgNodes} 
              expandedKeys={dfgExpanded} 
              onToggle={(e) => setDfgExpanded(e.value)} 
              showGridlines 
              style={{ width: '100%', fontSize: '0.8rem' }}
              tableStyle={{ tableLayout: 'fixed', width: '100%' }}
              filterMode="lenient"
            >
              <Column field="name" header={t('rtuDetail.diagnosticsTabs.columns.group')} expander style={{ width: '35%' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="rtuId" header={t('rtuDetail.diagnosticsTabs.columns.rtu')} style={{ width: '20%' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="status" header={t('rtuDetail.diagnosticsTabs.columns.status')} style={{ width: '20%' }} headerStyle={{ textAlign: 'center' }} bodyStyle={{ textAlign: 'center' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="type" header={t('rtuDetail.diagnosticsTabs.columns.type')} style={{ width: '25%' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
            </TreeTable>
          </div>
        </TabPanel>
        <TabPanel header={t('rtuDetail.diagnosticsTabs.headers.transfers')}>
          <div style={{ overflow: 'auto' }}>
            <TreeTable 
              value={ftNodes} 
              expandedKeys={ftExpanded} 
              onToggle={(e) => setFtExpanded(e.value)} 
              showGridlines 
              style={{ width: '100%', fontSize: '0.8rem' }}
              tableStyle={{ tableLayout: 'fixed', minWidth: '860px' }}
              filterMode="lenient"
            >
              <Column field="name" header={t('rtuDetail.diagnosticsTabs.columns.name')} expander style={{ width: '220px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="status" header={t('rtuDetail.diagnosticsTabs.columns.status')} style={{ width: '130px' }} headerStyle={{ textAlign: 'center' }} bodyStyle={{ textAlign: 'center' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="type" header={t('rtuDetail.diagnosticsTabs.columns.type')} style={{ width: '110px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="config" header={t('rtuDetail.diagnosticsTabs.columns.configuration')} style={{ width: '130px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="ip" header={t('rtuDetail.diagnosticsTabs.columns.ip')} style={{ width: '130px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="primaryIp" header={t('rtuDetail.diagnosticsTabs.columns.primaryIp')} style={{ width: '130px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
              <Column field="secondaryIp" header={t('rtuDetail.diagnosticsTabs.columns.secondaryIp')} style={{ width: '130px' }} filter={showFilters} filterPlaceholder="" showFilterMenu={false} />
            </TreeTable>
          </div>
        </TabPanel>
      </TabView>
    </Card>
  );
};

export default RTUDiagnosticsTabbedTree;
